import { sdk as imageKitSdk, } from '@wix/image-kit';
import { resizeThumbnail as _resizeThumbnail } from '@wix/communities-blog-universal/dist/src/utils/media/thumbnail';
import { isSSR as _isSSR } from './is-ssr';
const WIDTHS_MOBILE = [320, 375, 425, 768];
export const screenWidth = _isSSR() ? 980 : window.screen.width;
export const screenPixelRatio = _isSSR() ? 1 : window.devicePixelRatio;
export const getFormat = (isSSR) => !isSSR && window.__IS_WEBP_SUPPORTED__ ? 'webp' : 'png';
const snapToClosestScreenWidth = (w) => WIDTHS_MOBILE.reduce((closest, v) => {
    const d1 = v - w;
    const d2 = closest - w;
    return Math.abs(d1) < Math.abs(d2)
        ? d1 >= 0 || d2 < 0
            ? v
            : closest
        : closest;
});
const getProportionalHeight = (originalWidth, originalHeight, w) => w / (originalWidth / originalHeight);
const getProportionalWidth = (originalWidth, originalHeight, h) => h * (originalWidth / originalHeight);
const adjustSizeByPixelRatio = (originalWidth, originalHeight, w, h, pixelRatio) => {
    const nW = Math.ceil(w * pixelRatio);
    const nH = Math.ceil(h * pixelRatio);
    const isLargerThanOrig = nW > originalWidth || nH > originalHeight;
    return [
        isLargerThanOrig ? originalWidth : nW,
        isLargerThanOrig ? originalHeight : nH,
    ];
};
/**
 * Returns filled `[width, height]` of an image that is best for current device based on given params.
 */
export const getFillSize = (
/** original image width */
oW, 
/** original image height */
oH, 
/** new image width (the size that image will be shown) */
w, 
/** new image height (the size that image will be shown) */
h, isMobile, 
/** screenWidth, injected for testing, on live taken from window */
sW = screenWidth, 
/** pixelRatio, injected for testing, on live taken from window */
pR = screenPixelRatio) => {
    if (isMobile && (!w || w > sW)) {
        w = snapToClosestScreenWidth(sW);
    }
    if (w >= oW || h >= oH || !(w || h)) {
        return [oW, oH];
    }
    else if (w && h) {
        const propW = getProportionalWidth(oW, oH, h);
        const propH = getProportionalHeight(oW, oH, w);
        const scaleByW = propH >= h;
        return adjustSizeByPixelRatio(oW, oH, scaleByW ? w : propW, scaleByW ? propH : h, pR);
    }
    else if (w) {
        return adjustSizeByPixelRatio(oW, oH, w, getProportionalHeight(oW, oH, w), pR);
    }
    else if (h) {
        return adjustSizeByPixelRatio(oW, oH, getProportionalWidth(oW, oH, h), h, pR);
    }
    return undefined;
};
export const getImageUri = (image) => {
    if (typeof image === 'string') {
        return image;
    }
    if (image) {
        if ('id' in image && image.id !== '') {
            return image.id;
        }
        if ('file_name' in image) {
            return image.file_name;
        }
    }
    return undefined;
};
export const getImageUrl = ({ image, maxWidth = 1000, maxHeight = 1000, quality, type = 'fit', format, blur, encode = false, }) => {
    var _a, _b;
    if (!image) {
        return '';
    }
    const imageUri = (_a = getImageUri(image)) !== null && _a !== void 0 ? _a : '';
    if (!imageUri) {
        return 'url' in image ? (_b = image.url) !== null && _b !== void 0 ? _b : '' : '';
    }
    const imageSdkScaleFn = {
        fill: imageKitSdk.getScaleToFillImageURL,
        fit: imageKitSdk.getScaleToFitImageURL,
    }[type];
    const scaledImageUrl = imageSdkScaleFn(imageUri, image.width, image.height, maxWidth, maxHeight, {
        quality,
        preferredExtension: format,
        filters: { blur },
        autoEncode: encode,
    });
    return scaledImageUrl;
};
export const resizeThumbnail = ({ pathname, width, height, imageHost, }) => _resizeThumbnail(imageHost)(pathname, width, height);
const thumbnailOptionDefaults = {
    maxWidth: 150,
    maxHeight: 150,
};
export const getThumbnailUrlFromCoverMedia = (coverMedia, options = thumbnailOptionDefaults) => {
    var _a, _b, _c;
    const image = (coverMedia === null || coverMedia === void 0 ? void 0 : coverMedia.enabled)
        ? (_c = (_a = coverMedia.image) !== null && _a !== void 0 ? _a : (_b = coverMedia.video) === null || _b === void 0 ? void 0 : _b.thumbnail) !== null && _c !== void 0 ? _c : {}
        : {};
    return getImageUrl({
        image,
        maxWidth: options.maxWidth,
        maxHeight: options.maxHeight,
        quality: 100,
        format: 'webp',
    });
};
export const getThumbnailUrlFromMedia = (media, options = thumbnailOptionDefaults) => {
    var _a, _b, _c;
    let image;
    if (media === null || media === void 0 ? void 0 : media.displayed) {
        image =
            (_b = (_a = media.wixMedia) === null || _a === void 0 ? void 0 : _a.image) !== null && _b !== void 0 ? _b : (_c = media.embedMedia) === null || _c === void 0 ? void 0 : _c.thumbnail;
    }
    return getImageUrl({
        image,
        maxWidth: options.maxWidth,
        maxHeight: options.maxHeight,
        quality: 100,
        format: 'webp',
    });
};
export const preloadImage = (imageParams) => {
    return new Promise((resolve, reject) => {
        const img = new Image();
        img.src = getImageUrl(imageParams);
        if (img.complete) {
            resolve();
        }
        img.onload = () => resolve();
        img.onerror = () => reject();
    });
};
//# sourceMappingURL=image-utils.js.map